import React, { useState, useEffect, useRef } from 'react';
import soundtrack from './assets/soundtrack.mp3';
import './4096.css';

const GRID_SIZE = 4;
const CELL_SIZE = 80;
const CELL_GAP = 10;

// Creates a GRID_SIZE x GRID_SIZE matrix filled with zeros.
const createEmptyGrid = () => Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(0));

// Adds a new random tile in an available grid spot and advances the id counter.
const addRandomTile = (tiles, nextId) => {
  const occupied = new Set(tiles.map(t => `${t.y}-${t.x}`));
  const available = [];
  for (let y = 0; y < GRID_SIZE; y++) {
    for (let x = 0; x < GRID_SIZE; x++) {
      if (!occupied.has(`${y}-${x}`)) available.push({x, y});
    }
  }
  if (!available.length) return {newTiles: tiles, newNextId: nextId};
  const pos = available[Math.floor(Math.random() * available.length)];
  const value = Math.random() < 0.9 ? 2 : 4;
  const newTile = {id: nextId, x: pos.x, y: pos.y, value};
  return {newTiles: [...tiles, newTile], newNextId: nextId + 1};
};

// Compares two grids to determine if all cell values match.
const gridsEqual = (grid1, grid2) => {
  return grid1.every((row, i) => row.every((val, j) => val === grid2[i][j]));
};

// Converts tile objects into a numeric grid representation.
const getGrid = (tiles) => {
  const grid = createEmptyGrid();
  tiles.forEach(t => grid[t.y][t.x] = t.value);
  return grid;
};

// Returns a moved grid snapshot for move availability checks.
const moveGrid = (grid, direction) => {
  const result = createEmptyGrid();
  const coords = [];
  for (let y = 0; y < GRID_SIZE; y++) {
    for (let x = 0; x < GRID_SIZE; x++) {
      if (grid[y][x] !== 0) coords.push({x, y, value: grid[y][x]});
    }
  }
  const {newTiles} = moveTiles(coords, direction);
  newTiles.forEach(t => {
    result[t.y][t.x] = t.value;
  });
  return result;
};

// Moves tiles in the requested direction and returns merge metadata and score gain.
const moveTiles = (tiles, direction) => {
  let posKey = 'x';
  let otherKey = 'y';
  let sortAsc = (a, b) => a[posKey] - b[posKey];
  let sortDesc = (a, b) => b[posKey] - a[posKey];
  let sortFn = sortAsc;
  let increment = 1;
  let startPos = 0;
  if (direction === 'right') {
    sortFn = sortDesc;
    increment = -1;
    startPos = GRID_SIZE - 1;
  } else if (direction === 'up') {
    posKey = 'y';
    otherKey = 'x';
    sortFn = sortAsc;
    increment = 1;
    startPos = 0;
  } else if (direction === 'down') {
    posKey = 'y';
    otherKey = 'x';
    sortFn = sortDesc;
    increment = -1;
    startPos = GRID_SIZE - 1;
  } else if (direction === 'left') {
    sortFn = sortAsc;
    increment = 1;
    startPos = 0;
  }

  const lines = {};
  tiles.forEach(t => {
    const other = t[otherKey];
    if (!lines[other]) lines[other] = [];
    lines[other].push(t);
  });

  const newTiles = [];
  const mergeSources = [];
  let scoreAdd = 0;

  Object.keys(lines).forEach(other => {
    let lineTiles = lines[other].sort(sortFn);
    let i = 0;
    let targetPos = startPos;
    while (i < lineTiles.length) {
      let tile = {...lineTiles[i]};
      i++;
      if (i < lineTiles.length && lineTiles[i].value === tile.value) {
        let source = {...lineTiles[i]};
        i++;
        scoreAdd += tile.value * 2;
        tile[posKey] = targetPos;
        source[posKey] = targetPos;
        mergeSources.push(source);
        newTiles.push(tile);
      } else {
        tile[posKey] = targetPos;
        newTiles.push(tile);
      }
      targetPos += increment;
    }
  });

  return {newTiles, mergeSources, scoreAdd};
};

// Stores the previous value for comparison across renders.
const usePrevProps = (value) => {
  const ref = useRef();
  // Effect: update the stored previous value after each render.
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

// Maps tile values to synthwave-inspired colors.
const getTileColor = (value) => {
  if (value === 0) return 'transparent';
  const colors = {
    2: '#ff00ff',
    4: '#00ffff',
    8: '#ffff00',
    16: '#ff6600',
    32: '#00ff00',
    64: '#ff0000',
    128: '#9900ff',
    256: '#00ff99',
    512: '#ff0099',
    1024: '#0099ff',
    2048: '#ffffff',
  };
  return colors[value] || '#cccccc';
};

// Renders a single tile with animation and responsive sizing.
const Tile = ({ tile }) => {
  const prevValue = usePrevProps(tile.value);
  const hasChanged = prevValue !== undefined && prevValue !== tile.value;
  const [scale, setScale] = useState(1);

  // Effect: briefly scale the tile when its value changes.
  useEffect(() => {
    if (hasChanged) {
      setScale(1.1);
      setTimeout(() => setScale(1), 100);
    }
  }, [hasChanged]);

  const [width] = useWindowSize();
  let cellSize = 80;
  let cellGap = 10;
  if (width < 600) {
    cellSize = 60;
    cellGap = 6;
  }
  if (width < 400) {
    cellSize = 50;
    cellGap = 4;
  }

  const style = {
    left: tile.x * (cellSize + cellGap),
    top: tile.y * (cellSize + cellGap),
    width: `${cellSize}px`,
    height: `${cellSize}px`,
    transform: `scale(${scale})`,
    backgroundColor: getTileColor(tile.value),
    boxShadow: `0 0 10px ${getTileColor(tile.value)}, 0 0 20px ${getTileColor(tile.value)}`,
  };

  return (
    <div className="tile" style={style}>
      {tile.value}
    </div>
  );
};

// Tracks the current window size and updates on resize.
const useWindowSize = () => {
  const [size, setSize] = useState([window.innerWidth, window.innerHeight]);
  // Effect: listen for window resize events and update state.
  useEffect(() => {
    // Callback: update the stored window size.
    const update = () => setSize([window.innerWidth, window.innerHeight]);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return size;
};

// Renders the 4096 game UI and manages gameplay state, input, and audio.
const App = () => {
  // Builds the initial tile set and nextId for a fresh game.
  const initial = () => {
    let tiles = [];
    let nextId = 0;
    let temp = addRandomTile(tiles, nextId);
    tiles = temp.newTiles;
    nextId = temp.newNextId;
    temp = addRandomTile(tiles, nextId);
    tiles = temp.newTiles;
    nextId = temp.newNextId;
    return {tiles, nextId};
  };
  // Memoizes the initial game state so tiles and nextId stay consistent.
  const initialStateRef = useRef(null);
  if (!initialStateRef.current) {
    initialStateRef.current = initial();
  }
  const [tiles, setTiles] = useState(initialStateRef.current.tiles);
  const [nextId, setNextId] = useState(initialStateRef.current.nextId);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [inMotion, setInMotion] = useState(false);
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const [isSoundtrackPlaying, setIsSoundtrackPlaying] = useState(true);
  const soundtrackRef = useRef(null);
  const nextIdRef = useRef(nextId);

  // Effect: keep the nextId ref in sync with state.
  useEffect(() => {
    nextIdRef.current = nextId;
  }, [nextId]);

  // Starts or resumes the looping soundtrack without resetting playback.
  const ensureSoundtrackPlayback = () => {
    const audio = soundtrackRef.current;
    if (!audio || !isSoundtrackPlaying || !audio.paused) return;
    audio.play().catch(() => {
      setIsSoundtrackPlaying(false);
    });
  };

  // Toggles the soundtrack playback on user request.
  const handleToggleSoundtrack = () => {
    const audio = soundtrackRef.current;
    if (!audio) return;
    if (isSoundtrackPlaying) {
      audio.pause();
      setIsSoundtrackPlaying(false);
      return;
    }
    setIsSoundtrackPlaying(true);
    audio.play().catch(() => {
      setIsSoundtrackPlaying(false);
    });
  };

  // Creates the looping soundtrack element once and hooks user interaction for autoplay restrictions.
  useEffect(() => {
    if (soundtrackRef.current) return undefined;
    const audio = new Audio(soundtrack);
    audio.loop = true;
    audio.volume = 0.6;
    soundtrackRef.current = audio;

    ensureSoundtrackPlayback();
    // Callback: unlocks autoplay once the user interacts with the page.
    const handleFirstInteraction = () => {
      ensureSoundtrackPlayback();
      window.removeEventListener('pointerdown', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
      window.removeEventListener('touchstart', handleFirstInteraction);
    };
    window.addEventListener('pointerdown', handleFirstInteraction);
    window.addEventListener('keydown', handleFirstInteraction);
    window.addEventListener('touchstart', handleFirstInteraction);

    return () => {
      window.removeEventListener('pointerdown', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
      window.removeEventListener('touchstart', handleFirstInteraction);
      if (soundtrackRef.current) {
        soundtrackRef.current.pause();
      }
      soundtrackRef.current = null;
    };
  }, []);

  // Effect: sync soundtrack playback when the toggle changes.
  useEffect(() => {
    const audio = soundtrackRef.current;
    if (!audio) return;
    if (isSoundtrackPlaying) {
      audio.play().catch(() => {
        setIsSoundtrackPlaying(false);
      });
      return;
    }
    audio.pause();
  }, [isSoundtrackPlaying]);

  // Effect: scope the page-level visuals to the 4096 view only.
  useEffect(() => {
    document.body.classList.add('game-4096-body');
    return () => {
      document.body.classList.remove('game-4096-body');
    };
  }, []);

  // Effect: determine if the game is over when tiles update.
  useEffect(() => {
    const grid = getGrid(tiles);
    const isFull = tiles.length === GRID_SIZE * GRID_SIZE;
    if (isFull) {
      const hasMoves = ['up', 'down', 'left', 'right'].some(dir => !gridsEqual(grid, moveGrid(grid, dir)));
      if (!hasMoves) setGameOver(true);
    }
  }, [tiles]);

  // Effect: show a win notification when 2048 is reached.
  useEffect(() => {
    if (tiles.some(t => t.value === 2048)) alert('You won!');
  }, [tiles]);

  // Handles a move request and orchestrates merges, scoring, and new tile insertion.
  const handleMove = (direction) => {
    if (inMotion || gameOver) return;
    const {newTiles, mergeSources, scoreAdd} = moveTiles(tiles, direction);
    const movedGrid = getGrid(newTiles);
    const currentGrid = getGrid(tiles);
    const hasMoved = !gridsEqual(currentGrid, movedGrid) || mergeSources.length > 0;
    if (!hasMoved) return;
    setInMotion(true);
    setTiles([...newTiles, ...mergeSources]);
    setScore(prev => prev + scoreAdd);
    // Timeout: allow merge animation before combining values.
    setTimeout(() => {
      setTiles(prev => {
        let updated = prev.map(t => ({...t}));
        mergeSources.forEach(source => {
          const target = updated.find(t => t.id !== source.id && t.x === source.x && t.y === source.y);
          if (target) target.value += source.value;
        });
        updated = updated.filter(t => !mergeSources.some(s => s.id === t.id));
        return updated;
      });
      // Timeout: insert a new random tile after merges complete.
      setTimeout(() => {
        setTiles(prev => {
          if (prev.length >= GRID_SIZE * GRID_SIZE) return prev;
          const {newTiles: addedTiles, newNextId} = addRandomTile(prev, nextIdRef.current);
          nextIdRef.current = newNextId;
          setNextId(newNextId);
          return addedTiles;
        });
        setInMotion(false);
      }, 0);
    }, 250);
  };

  // Effect: bind arrow key input to move handling.
  useEffect(() => {
    // Callback: translate key presses into game moves.
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowUp': handleMove('up'); break;
        case 'ArrowDown': handleMove('down'); break;
        case 'ArrowLeft': handleMove('left'); break;
        case 'ArrowRight': handleMove('right'); break;
        default: break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [inMotion, gameOver]);

  // Captures the initial position of a touch gesture.
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY });
  };

  // Converts the touch delta into a move direction.
  const handleTouchEnd = (e) => {
    if (inMotion || gameOver || !e.changedTouches) return;
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStart.x;
    const deltaY = touch.clientY - touchStart.y;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (absX > absY && absX > 30) {
      handleMove(deltaX > 0 ? 'right' : 'left');
    } else if (absY > absX && absY > 30) {
      handleMove(deltaY > 0 ? 'down' : 'up');
    }
  };

  // Resets all gameplay state to a fresh start.
  const resetGame = () => {
    const init = initial();
    setTiles(init.tiles);
    setNextId(init.nextId);
    nextIdRef.current = init.nextId;
    setScore(0);
    setGameOver(false);
  };

  const [width] = useWindowSize();
  let cellSize = 80;
  let cellGap = 10;
  if (width < 600) {
    cellSize = 60;
    cellGap = 6;
  }
  if (width < 400) {
    cellSize = 50;
    cellGap = 4;
  }

  return (
    <div className="app game-4096" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <h1 className="title">4096/2 Synthwave</h1>
      <div className="score">Score: {score}</div>
      <div className="grid-container" style={{width: `${GRID_SIZE * cellSize + (GRID_SIZE - 1) * cellGap}px`}}>
        <div className="grid" style={{gridTemplateColumns: `repeat(4, ${cellSize}px)`, gridGap: `${cellGap}px`}}>
          {Array(16).fill(0).map((_, index) => (
            <div key={index} className="cell" style={{width: `${cellSize}px`, height: `${cellSize}px`}} />
          ))}
        </div>
        <div className="tile-container">
          {tiles.map(tile => (
            <Tile key={tile.id} tile={tile} />
          ))}
        </div>
      </div>
      <button className="soundtrack-toggle" onClick={handleToggleSoundtrack}>
        {isSoundtrackPlaying ? 'Pause music' : 'Play music'}
      </button>
      {gameOver && (
        <div className="game-over">
          <p>Game Over!</p>
          <button onClick={resetGame}>Restart</button>
        </div>
      )}
    </div>
  );
};

export default App;
